// Lil-calendar helpers for the Transactions page. Uses the *lite* bundle
// (≈±500 yr around build time, plenty for tx browsing).
import { gregorianToPsc, pscToGregorian, pscObjectToString, lilToHe, heToLil } from '@justhyur/lil-calendar'

export { lilToHe, heToLil }

const ROMAN = ['', 'I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII','XIII']

function isoDay(d) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

// Lil month shape:
//   `{ year, month: 1..13 }` — one of the 13 standard 28-day months
//   `{ year, special: true }` — slot for DR+PH (days outside the 13×28 grid),
//                               displayed BETWEEN month XIII of year N and
//                               month I of year N+1.

function pscYearStr(year) {
  return year >= 0
    ? String(year).padStart(5, '0')
    : '-' + String(Math.abs(year)).padStart(5, '0')
}

// Returns ISO gregorian dates for DR (null if not a leap year) and PH of the
// given Lil year.
function specialDates(year) {
  let drIso = null, phIso = null
  try { drIso = pscToGregorian(`PSC${pscYearStr(year)}DR`) } catch {}
  try { phIso = pscToGregorian(`PSC${pscYearStr(year)}PH`) } catch {}
  return { drIso, phIso }
}

// `psc` from gregorianToPsc(): { year, month: 1..13, day: 1..28 }
//   OR { year, day: 'PH'|'DR' } (special days outside the 13×28 grid)
export function todayLil() {
  const psc = gregorianToPsc(new Date())
  if (typeof psc.day === 'string') {
    // PH/DR live in the dedicated special slot
    return { year: psc.year, special: true }
  }
  return { year: psc.year, month: psc.month }
}

// ±1 only (the only call sites — swipe + arrow). Forward from XIII passes
// through the special slot before reaching month I of the next year.
export function shiftLilMonth(current, delta) {
  if (current.special) {
    return delta > 0
      ? { year: current.year + 1, month: 1 }
      : { year: current.year,     month: 13 }
  }
  const m = current.month + delta
  if (m > 13) return { year: current.year,     special: true }
  if (m < 1)  return { year: current.year - 1, special: true }
  return { year: current.year, month: m }
}

// [from, to) Gregorian range for a viewed Lil month, or for the special slot.
export function lilMonthRange(viewed) {
  if (viewed.special) return lilSpecialRange(viewed.year)
  const { year, month } = viewed
  const fromStr = pscObjectToString({ year, month, day: 1 })
  const from = pscToGregorian(fromStr)

  if (month < 13) {
    const next = shiftLilMonth({ year, month }, 1)
    const to = pscToGregorian(pscObjectToString({ year: next.year, month: next.month, day: 1 }))
    return { from, to }
  }
  // Month 13: upper bound is the first special day (DR if leap, else PH).
  // PH/DR no longer appear inside month 13 — they live in the special slot.
  const { drIso, phIso } = specialDates(year)
  return { from, to: drIso || phIso }
}

// Range covering DR (if leap) and PH for the given year.
export function lilSpecialRange(year) {
  const { drIso, phIso } = specialDates(year)
  if (!phIso) return { from: null, to: null }
  const from = drIso || phIso
  const phDate = new Date(phIso)
  phDate.setUTCDate(phDate.getUTCDate() + 1)
  return { from, to: isoDay(phDate) }
}

// Formats just the year part using the active era notation ('lil' → L10210,
// 'he' → HE12026 for the Holocene Era).
export function formatLilYear(lilYear, era = 'lil') {
  if (era === 'he') return `HE${lilToHe(lilYear)}`
  return `L${lilYear}`
}

// JSX variant: wraps the era prefix ('L' / 'HE') in its own span so the UI
// can style it muted+smaller without losing semantics.
export function formatLilYearJsx(lilYear, era = 'lil') {
  const prefix = era === 'he' ? 'HE' : 'L'
  const num    = era === 'he' ? lilToHe(lilYear) : lilYear
  return (
    <>
      <span className="lil-era">{prefix}</span>{num}
    </>
  )
}

export function formatLilMonth(viewed, era = 'lil') {
  if (viewed.special) {
    return `${formatLilYear(viewed.year, era)} – ${formatLilYear(viewed.year + 1, era)}`
  }
  return `${ROMAN[viewed.month]} · ${formatLilYear(viewed.year, era)}`
}

export function formatLilMonthJsx(viewed, era = 'lil') {
  if (viewed.special) {
    return (
      <>
        {formatLilYearJsx(viewed.year, era)} – {formatLilYearJsx(viewed.year + 1, era)}
      </>
    )
  }
  return (
    <>
      {ROMAN[viewed.month]} · {formatLilYearJsx(viewed.year, era)}
    </>
  )
}

// Lil "day key" usable for grouping (one per visual row in the list).
// Returns a stable string per Lil day; PH/DR get their own keys.
export function lilDayKey(isoDate) {
  const psc = gregorianToPsc(new Date(isoDate))
  const y = String(psc.year).padStart(5, '0')
  if (typeof psc.day === 'string') return `${y}-X-${psc.day}` // PH or DR
  return `${y}-${String(psc.month).padStart(2, '0')}-${String(psc.day).padStart(2, '0')}`
}

// Returns the info needed to render a Lil day header for a tx date.
export function lilDayInfo(isoDate) {
  const psc = gregorianToPsc(new Date(isoDate))
  if (typeof psc.day === 'string') {
    return { year: psc.year, monthRoman: ROMAN[13], special: psc.day, label: psc.day }
  }
  return { year: psc.year, month: psc.month, monthRoman: ROMAN[psc.month], day: psc.day }
}
